import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { categories, articleCards } from '@/mocks/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const guidesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/90 backdrop-blur-md border-b border-background-200/70">
      <nav className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-stack-fill text-background-50 text-lg"></i>
          </div>
          <span className="font-heading font-bold text-base lg:text-lg text-foreground-900 whitespace-nowrap">
            Sugargoo <span className="text-primary-500">Spreadsheet</span> Hub
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-5">
          <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-home-4-line mr-1"></i>
            Home
          </Link>

          <a href="#categories" className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
            Categories
          </a>

          <div className="relative" ref={guidesRef}>
            <button
              onClick={() => setGuidesOpen(!guidesOpen)}
              className="flex items-center gap-1 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line mr-1"></i>
              Shopping Guides
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${guidesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {guidesOpen && (
              <div className="absolute top-full mt-2 left-0 bg-background-50 border border-background-200/70 rounded-xl shadow-lg overflow-hidden z-50 w-72 py-1">
                {articleCards.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    onClick={() => setGuidesOpen(false)}
                    className="flex items-start gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary-100 text-primary-700 flex-shrink-0 mt-0.5 whitespace-nowrap">
                      {article.category}
                    </span>
                    <div>
                      <span className="block text-sm font-medium text-foreground-700 leading-tight whitespace-nowrap">
                        {article.title}
                      </span>
                      <span className="block text-xs text-foreground-400 mt-0.5">{article.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <a href="#faq" className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
            FAQ
          </a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Visit Main Site
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>
        </div>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <i className="ri-close-line text-xl"></i>
          ) : (
            <i className="ri-menu-line text-xl"></i>
          )}
        </button>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-primary-500 text-lg"></i>
              <span className="text-sm font-semibold text-foreground-700">Home</span>
            </Link>

            <a
              href="#categories"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-grid-line text-primary-500 text-lg"></i>
              <span className="text-sm font-semibold text-foreground-700">Categories</span>
            </a>

            <button
              onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <i className="ri-price-tag-3-line text-secondary-500 text-lg"></i>
                <span className="text-sm font-semibold text-foreground-700">Browse All Categories</span>
              </div>
              {mobileCatsOpen ? (
                <i className="ri-arrow-up-s-line text-foreground-400"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-foreground-400"></i>
              )}
            </button>

            {mobileCatsOpen && (
              <div className="ml-4 pl-3 border-l-2 border-background-200/70 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className={`${cat.icon} text-foreground-400 text-sm`}></i>
                    <span className="text-xs font-medium text-foreground-600 whitespace-nowrap">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}

            <button
              onClick={() => setMobileGuidesOpen(!mobileGuidesOpen)}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <i className="ri-book-open-line text-accent-500 text-lg"></i>
                <span className="text-sm font-semibold text-foreground-700">Shopping Guides</span>
              </div>
              {mobileGuidesOpen ? (
                <i className="ri-arrow-up-s-line text-foreground-400"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-foreground-400"></i>
              )}
            </button>

            {mobileGuidesOpen && (
              <div className="ml-4 pl-3 border-l-2 border-background-200/70 space-y-0.5">
                {articleCards.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="px-1 py-0.5 rounded text-[10px] font-semibold bg-primary-100 text-primary-700 flex-shrink-0 whitespace-nowrap">
                      {article.category}
                    </span>
                    <span className="text-xs font-medium text-foreground-600 leading-tight">{article.title}</span>
                  </Link>
                ))}
              </div>
            )}

            <a
              href="#faq"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-question-answer-line text-primary-500 text-lg"></i>
              <span className="text-sm font-semibold text-foreground-700">FAQ</span>
            </a>

            <div className="pt-2 mt-2 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Visit Main Site
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}